<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/vue-3';
import { useApolloClient } from '@vue/apollo-composable';

import { UFileAttachment } from '../../UFileAttachment';
import type { FileAttachmentFile } from '../../UFileAttachment/types';
import { formatFileSize } from '../../UFileUploader/types';
import { openLightboxFromImage } from '../../UImageLightbox/lightbox-store';
import { clearUpload, getUploadState } from './upload-registry';
import {
  ensureFreshBinaryFileUrls,
  holdBinaryFileUrls,
  refreshBinaryFileUrls,
  resolveBinaryFileUrls,
} from './useBinaryFileHydration';

const props = defineProps<NodeViewProps>();

const { client } = useApolloClient();

const fileId = computed(() => props.node.attrs.fileId as string | null);
const uploadId = computed(() => props.node.attrs.uploadId as string | null);

const uploadState = computed(() => getUploadState(uploadId.value));

const hydrated = computed(() => (fileId.value ? resolveBinaryFileUrls(client, fileId.value) : null));

// While this view shows the file, its URLs are renewed in the background.
watch(fileId, (id, _previous, onCleanup) => {
  if (id) {
    onCleanup(holdBinaryFileUrls(id));
  }
}, { immediate: true });

const isUploading = computed(() => !fileId.value && uploadState.value?.status === 'uploading');

/**
 * Error cases: the upload itself failed, the tab that started the upload was
 * closed before it finished (uploadId present but no registry entry), or the
 * file lookup failed after the fact.
 */
const isError = computed(() => {
  if (!fileId.value) {
    return !isUploading.value;
  }

  return hydrated.value?.status === 'error';
});

const errorMessage = computed(() => {
  if (uploadState.value?.errorMessage) {
    return uploadState.value.errorMessage;
  }

  if (!fileId.value && !isUploading.value) {
    return 'The upload was interrupted. Remove the file and try again.';
  }

  if (hydrated.value?.status === 'error') {
    return 'Failed to load the file.';
  }

  return '';
});

const file = computed<FileAttachmentFile>(() => ({
  url: hydrated.value?.url,
  downloadUrl: hydrated.value?.downloadUrl,
  meta: {
    name: props.node.attrs.name as string,
    size: props.node.attrs.size as number,
    mimeType: props.node.attrs.mimeType as string,
  },
}));

/** The card's download goes out with URLs renewed first if the cached ones are near expiry. */
const resolveDownloadUrl = async (): Promise<string | null | undefined> => {
  if (!fileId.value) {
    return null;
  }

  const fresh = await ensureFreshBinaryFileUrls(client, fileId.value);

  return fresh?.downloadUrl ?? fresh?.url;
};

/**
 * The URL a thumbnail could not render after its one refetch — a format the
 * browser cannot decode (HEIC). Keyed on the URL, like the image node, so a
 * fresh one gets its own attempt.
 */
const failedSrc = ref<string | null>(null);

/**
 * An image file read in a finished document — an email's attached photos —
 * is a thumbnail that opens the lightbox, not a row to download from. Tiles
 * are inline blocks, so attachments side by side read as one group, and they
 * join the same gallery as the images in the text. In the editor a card stays
 * a card: there it is something being attached, with a remove button.
 */
const isTile = computed(() =>
  !props.editor.isEditable
  && Boolean(fileId.value)
  && String(props.node.attrs.mimeType ?? '').startsWith('image/')
  && hydrated.value?.status !== 'error'
  && (failedSrc.value === null || failedSrc.value !== hydrated.value?.url));

const tileSrc = computed(() => (hydrated.value?.status === 'ready' ? hydrated.value.url ?? null : null));

const tileLabel = computed(() => {
  const size = Number(props.node.attrs.size) || 0;

  return size > 0 ? `${file.value.meta.name} (${formatFileSize(size)})` : file.value.meta.name ?? '';
});

const onTileError = (event: Event) => {
  const attemptedSrc = (event.target as HTMLImageElement).getAttribute('src');

  if (!fileId.value || !refreshBinaryFileUrls(client, fileId.value)) {
    failedSrc.value = attemptedSrc;
  }
};

const openTile = (event: MouseEvent) => {
  const image = (event.currentTarget as HTMLElement).querySelector('img');

  if (image) {
    openLightboxFromImage(image);
  }
};

const onRemove = () => {
  if (uploadId.value) {
    clearUpload(uploadId.value);
  }

  props.deleteNode();
};
</script>

<template>
  <!-- One wrapper whichever way it renders: ProseMirror holds on to the node
       view's root element, so swapping it would leave the document pointing at
       a detached one. -->
  <NodeViewWrapper
    data-testid="editor-attachment-file"
    :data-attachment-tile="isTile ? '' : undefined"
    :class="isTile
      ? 'my-1 me-2 inline-block max-w-full align-top'
      : ['my-2', { 'rounded-[10px] ring-2 ring-primary': props.selected && props.editor.isEditable }]"
  >
    <template v-if="isTile">
      <!-- A fixed box, filled before the URL arrives, so a thread scrolled to
           its end does not jump as the thumbnails load. -->
      <button
        v-if="tileSrc"
        type="button"
        class="block cursor-zoom-in overflow-hidden rounded-lg ring-1 ring-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        :title="`${tileLabel} — click to open`"
        :aria-label="`Open ${tileLabel}`"
        data-testid="editor-attachment-tile"
        @click="openTile"
      >
        <img
          :src="tileSrc"
          :alt="file.meta.name ?? ''"
          class="h-32 w-44 max-w-full object-cover"
          loading="lazy"
          draggable="false"
          data-lightbox=""
          :data-lightbox-download="hydrated?.downloadUrl ?? undefined"
          :data-lightbox-name="file.meta.name ?? undefined"
          :data-lightbox-file-id="fileId ?? undefined"
          data-testid="editor-attachment-tile-image"
          @error="onTileError"
        >
      </button>
      <div
        v-else
        class="h-32 w-44 max-w-full animate-pulse rounded-lg bg-accented"
      />
    </template>

    <UFileAttachment
      v-else
      :file="file"
      :clearable="props.editor.isEditable"
      :uploading="isUploading"
      :error="isError"
      :error-message="errorMessage"
      :resolve-download-url="resolveDownloadUrl"
      @remove="onRemove"
    />
  </NodeViewWrapper>
</template>
